'use client';

import { useContext, useEffect, useRef, useState } from "react";
import './home-mobile.scss';
import LoadingCapabilities from "./LoadingCapabilities";
import Intro from "./Intro";
import Reach from "./Reach";
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { PAGE_MOBILE } from "@/lib/constants";
import { Swiper, SwiperSlide } from "swiper/react";
import { ProjectModel } from "@/lib/models/Project";
import { usePathname } from "next/navigation";
import ProjectMobileItem from "./ProjectMobileItem";
import FooterMobile from "@/components/Mobile/FooterMobile";
import dynamic from "next/dynamic";
import { RouterName } from "@/lib/enum/RouterEnum";
import { getNavState, setNavState } from "@/lib/utils/navState";
import { useTransitionExit } from "@/components/Mobile/Transition/SwipeTransition";
import TransitionContext from "@/components/Mobile/Transition/TransitionContext";

const Title = dynamic(() => import("./Title"), { ssr: false });

interface Props {
  projects: ProjectModel[];
}

const HomeMobile = ({ projects }: Props) => {
  const swiperRef = useRef<any>(null);
  const loadingBorderResetTimerRef = useRef<number | null>(null);
  const loadingToTitleDragRef = useRef(false);
  const pathname = usePathname();
  const exit = useTransitionExit();
  const { markRouteReady } = useContext(TransitionContext);
  const [initialSlide] = useState(PAGE_MOBILE.TITLE);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number | null>(initialSlide);
  const [footer, setFooter] = useState(initialSlide);
  const [windowHeight, setWindowHeight] = useState(0);
  const [swiperReady, setSwiperReady] = useState(false);
  const [restorationApplied, setRestorationApplied] = useState(false);
  const [loadingBorderOffset, setLoadingBorderOffset] = useState<number | null>(null);

  const _getCurrentProject = () => {
    if (currentSlideIndex) {
      const project: ProjectModel | undefined = projects.find(item => item.order === currentSlideIndex - PAGE_MOBILE.PROJECTS);
      return project;
    }

    return undefined;
  }

  // Navigates to project details of current project.
  const _changeShowDetail = () => {
    const project = _getCurrentProject();
    if (project) {
      let charExits = /\W/g;
      const path = project.title.replaceAll(" ", "_").replace(charExits, '');
      if (currentSlideIndex !== null) setNavState(currentSlideIndex);
      exit(`${RouterName.projectDetail}/${path}`);
    }
  };

  // Slide to the slide we were at before we navigated into project details.
  useEffect(() => {
    const saved = getNavState<number>();
    if (typeof saved === 'number') {
      swiperRef.current?.slideToLoop(saved, 0);
    }
    setRestorationApplied(true);
  }, [pathname]);

  useEffect(() => {
    if (swiperReady && restorationApplied) {
      markRouteReady(pathname);
    }
  }, [swiperReady, restorationApplied, pathname, markRouteReady]);

  const _handleNextStep = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const clearLoadingBorderTimer = () => {
    if (loadingBorderResetTimerRef.current !== null) {
      window.clearTimeout(loadingBorderResetTimerRef.current);
      loadingBorderResetTimerRef.current = null;
    }
  };

  const commitSwiperState = (swiper: any) => {
    if (!swiper || swiper.realIndex === currentSlideIndex) return;

    setCurrentSlideIndex(swiper.realIndex);
    setFooter(swiper.realIndex);
    setNavState(swiper.realIndex);
  };

  const resetLoadingBorder = (swiper?: any) => {
    clearLoadingBorderTimer();
    loadingToTitleDragRef.current = false;
    setLoadingBorderOffset(null);

    if (swiper) {
      swiper.allowTouchMove = swiper.realIndex !== PAGE_MOBILE.TITLE;
      commitSwiperState(swiper);
    }
  };

  const getLoadingSlideProgress = (swiper: any) => {
    const activeSlide = swiper.slides?.[swiper.activeIndex];
    if (activeSlide?.getAttribute('data-swiper-slide-index') === String(PAGE_MOBILE.LOADING)) {
      return activeSlide.progress ?? 0;
    }

    const loadingSlides = (Array.from(swiper.slides ?? []) as any[])
      .filter((slide: any) => slide.getAttribute('data-swiper-slide-index') === String(PAGE_MOBILE.LOADING));

    if (!loadingSlides.length) return 0;

    return loadingSlides.reduce((closest: any, slide: any) => {
      return Math.abs(slide.progress ?? 0) < Math.abs(closest.progress ?? 0) ? slide : closest;
    }, loadingSlides[0]).progress ?? 0;
  };

  const syncLoadingBorder = (swiper: any) => {
    if (!loadingToTitleDragRef.current) return;

    const progress = getLoadingSlideProgress(swiper);
    const width = swiper.width || window.innerWidth;
    const offset = Math.max(0, Math.min(width, -progress * width));

    setLoadingBorderOffset(offset);
  };

  const handleSliderMove = (swiper: any) => {
    const diff = swiper.touches?.diff ?? 0;
    const isLoadingToTitleDrag = swiper.realIndex === PAGE_MOBILE.LOADING && diff > 0;

    if (!isLoadingToTitleDrag) {
      if (!loadingToTitleDragRef.current) resetLoadingBorder(swiper);
      return;
    }

    clearLoadingBorderTimer();
    loadingToTitleDragRef.current = true;
    syncLoadingBorder(swiper);
  };

  const handleTouchEnd = (swiper: any) => {
    if (!loadingToTitleDragRef.current) return;

    syncLoadingBorder(swiper);
    clearLoadingBorderTimer();
    loadingBorderResetTimerRef.current = window.setTimeout(() => resetLoadingBorder(swiper), 450);
  };

  const changeSwiper = (swiper: any) => {
    if (loadingToTitleDragRef.current) return;

    // GX: Added if check to skip unneccessary re-renders
    commitSwiperState(swiper);
  };

  const _handlePrevStep = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const isTitle = initialSlide === PAGE_MOBILE.TITLE;
    if (swiperRef.current) {
      swiperRef.current.allowTouchMove = !isTitle;
    }
  }, [initialSlide]);

  const isTitleSlide = currentSlideIndex === PAGE_MOBILE.TITLE;

  const _renderProjectItem = (item: ProjectModel, index: number) => {
    return (
      <SwiperSlide 
        virtualIndex={index + PAGE_MOBILE.PROJECTS} 
        key={index + PAGE_MOBILE.PROJECTS}
      >
        <ProjectMobileItem
          height={windowHeight}
          index={index}
          project={item}
          goToDetail={_changeShowDetail}
          realIndex={currentSlideIndex}
        />
      </SwiperSlide>
    )
  };

  const _renderFooter = () => {
    return (
      <FooterMobile
        footerType={footer}
        footerColor={_getCurrentProject()?.titleColor}
        handlePrevStep={_handlePrevStep}
        handleNextStep={_handleNextStep}
        listProject={projects}
        hideBorder={loadingBorderOffset !== null}
        hideText={loadingBorderOffset !== null}
      />
    )
  };

  useEffect(() => {
    return () => clearLoadingBorderTimer();
  }, []);

  return (
    <div className={`home-intro-mobile ${isTitleSlide ? 'is-title-slide' : ''}`}>
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        className='swiper-container'
        ref={swiperRef}
        onSwiper={(swiper: any) => {
          swiperRef.current = swiper;
          setSwiperReady(true);
        }}
        onSlideChange={changeSwiper}
        onProgress={syncLoadingBorder}
        onSliderMove={handleSliderMove}
        onTouchEnd={handleTouchEnd}
        onTransitionEnd={resetLoadingBorder}
        initialSlide={initialSlide}
        loop
        watchSlidesProgress
        onActiveIndexChange={(swiper: any) => {
          if (loadingToTitleDragRef.current) return;

          swiper.allowTouchMove = swiper.realIndex !== PAGE_MOBILE.TITLE;
        }}
      >
        <SwiperSlide virtualIndex={PAGE_MOBILE.TITLE} className="title-panel">
          <Title
            key={PAGE_MOBILE.TITLE}
            handleNextStep={_handleNextStep}
            isActive={isTitleSlide}
          />
        </SwiperSlide>
        <SwiperSlide virtualIndex={PAGE_MOBILE.LOADING}>
          <LoadingCapabilities
            key={PAGE_MOBILE.LOADING}
          />
        </SwiperSlide>
        <SwiperSlide virtualIndex={PAGE_MOBILE.INTRO}>
          <Intro
            key={PAGE_MOBILE.INTRO}
          />
        </SwiperSlide>
        {projects.map((item, index) => {
          return _renderProjectItem(item, index)
        })}
        <SwiperSlide virtualIndex={PAGE_MOBILE.REACH + projects.length}>
          <Reach
            key={PAGE_MOBILE.REACH}
            footerType={PAGE_MOBILE.REACH}
          />
        </SwiperSlide>
      </Swiper>
      {loadingBorderOffset !== null && (
        <div
          className="loading-footer-follow"
          style={{ transform: `translate3d(${loadingBorderOffset}px, 0, 0)` }}
        >
          <div className="footer-mobile-border" />
          <div className="loading-footer-follow-text">
            <p className="letter-scrambler">A small lab with big experiments.</p>
          </div>
        </div>
      )}
      {_renderFooter()}
    </div>
  )
};

export default HomeMobile;
